import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

export interface FieldDefinition {
  label?: string;
  type: 'string' | 'number' | 'boolean';
  editable?: boolean;
  columnPos?: number;
}

export interface TableRow {
  __id: string;
  [key: string]: any;
}

@Component({
  selector: 'sm-table-groups',
  standalone: true,
  imports: [TableModule, ButtonModule, InputTextModule, InputNumberModule, CheckboxModule, FormsModule, CommonModule, TranslateModule],
  templateUrl: './table-groups.component.html',
  styleUrls: ['./table-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TableGroupsComponent implements OnInit, OnChanges, OnDestroy {
  private _singular = 'item';
  @Input()
  set singular(v: string | null | undefined) {
    this._singular = v ?? 'item';
  }
  get singular(): string {
    return this._singular;
  }

  @Input({ required: true }) value!: Observable<any[]>;
  @Input({ required: true }) structure!: Record<string, FieldDefinition>;
  @Input() onChange?: (rows: any[]) => void;
  @Input() disabled = false;
  @Input() error: string[] = [];

  readonly rows$ = new BehaviorSubject<TableRow[]>([]);
  private sub?: Subscription;

  sortedStructure: { key: string; value: FieldDefinition }[] = [];

  ngOnInit(): void {
    // Initialize sorted structure if available at startup
    if (this.structure) {
      this.updateSortedStructure();
    }

    this.sub = this.value.subscribe(arr => {
      if (!arr) return;
      const withIds = arr.map(row => ({ __id: this.generateId(), ...row }));
      this.rows$.next(withIds);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['structure'] && this.structure) {
      this.updateSortedStructure();
    }

    if (changes['value'] && changes['value'].currentValue) {
      const arr = changes['value'].currentValue as any[];
      const withIds = arr.map(row => ({ __id: this.generateId(), ...row }));
      this.rows$.next(withIds);
    }
  }

  private updateSortedStructure(): void {
    this.sortedStructure = Object.entries(this.structure || {})
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => (a.value.columnPos ?? 0) - (b.value.columnPos ?? 0));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  add(): void {
    const blank: TableRow = {
      __id: this.generateId(),
      ...Object.fromEntries(Object.entries(this.structure).map(([k, def]) => [k, def.type === 'number' ? 0 : def.type === 'boolean' ? false : ''])),
    };
    const current = this.rows$.value;
    this.rows$.next([...current, blank]);
    this.emit();
  }

  update(row: TableRow, key: string, val: any): void {
    row[key] = val;
    this.emit();
  }

  remove(index: number): void {
    const current = this.rows$.value;
    current.splice(index, 1);
    this.rows$.next([...current]);
    this.emit();
  }

  clearAll(): void {
    this.rows$.next([]);
    this.emit();
  }

  trackByRow(index: number, row: TableRow): string {
    return row.__id;
  }

  emit(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const plainRows = this.rows$.value.map(({ __id, ...rest }) => rest);
    this.onChange?.(plainRows);
  }

  onInputBlur(event: Event, row: TableRow, key: string): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    this.update(row, key, target.value);
  }

  onInputEnter(event: Event, row: TableRow, key: string): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    this.update(row, key, target.value);
    target.blur();
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }
}
